import { UtilService } from './../../../services/util.service';
import { Component, inject, Input, OnChanges } from '@angular/core';
import * as Highcharts from 'highcharts';
import { HighchartsChartModule } from 'highcharts-angular';
import { LoaderComponent } from '../../../shared/components/loader/loader.component';
import { NoWidgetDataComponent } from '../../my-brand-screen/no-widget-data/no-widget-data.component';
import { AiPresenceAdvertisingImpactResponse } from '../types';
import { CommonModule } from '@angular/common';
import { ShortDisplayValuePipe } from '../../../pipes/short-display-value.pipe';
import { addPercent } from '../../../utils/numbers';

@Component({
  selector: 'app-advertising-impact',
  imports: [
    CommonModule,
    HighchartsChartModule,
    LoaderComponent,
    NoWidgetDataComponent,
  ],
  providers: [
    ShortDisplayValuePipe
  ],
  templateUrl: './advertising-impact.component.html',
  styleUrl: './advertising-impact.component.scss'
})
export class AdvertisingImpactComponent implements OnChanges {
  @Input() loading = false;
  @Input() data?: AiPresenceAdvertisingImpactResponse;

  private utilService = inject(UtilService);

  private style = {
    fontFamily: 'Poppins',
    fontSize: '12px',
    fontStyle: 'normal',
    fontWeight: '400',
    lineHeight: '18px',
    color: '#686D75'
  };

  private shortDisplayValuePipe = inject(ShortDisplayValuePipe);
  public containerId = 'advertising-impact-chart-container';
  public chartInstance: Highcharts.Chart | undefined;
  public Highcharts: typeof Highcharts = Highcharts;
  public chartOptions: Highcharts.Options = {
    chart: {
      type: 'line',
      height: 356,
      marginTop: 40,
      spacingLeft: 0,
      spacingRight: 0,
      marginLeft: 65,
      marginRight: 65,
      animation: false,
    },
    title: {
      text: undefined
    },
    credits: {
      enabled: false
    },
    tooltip: {
      enabled: false
    },
    xAxis: {
      title: { text: null },
      labels: { style: this.style }
    },
    yAxis: [
      {
        title: { text: null, align: 'high', rotation: 0, style: this.style, textAlign: 'left', y: -24 },
        labels: { style: this.style },
        opposite: false
      },
      {
        title: { text: null, align: 'high', rotation: 0, style: this.style, textAlign: 'right', y: -24 },
        labels: { style: this.style },
        opposite: true
      }
    ],
    plotOptions: {
      series: {
        marker: {
          enabled: false,
          symbol: 'circle',
          states: {
            hover: {
              enabled: true,
              width: 12,
              height: 12,
            }
          },
        },
        showInLegend: true
      }
    },
    legend: {
      layout: 'horizontal',
      align: 'left',
      verticalAlign: 'bottom',
      itemStyle: this.style,
      useHTML: true,
      symbolWidth: 0,
      labelFormatter: function () {
        return `<span style="
                  display: inline-block;
                  width: 10px;
                  height: 10px;
                  background-color: ${this.color};
                  border-radius: 50%;
                  margin-right: 3px;
                  margin-left: 5px;
                "></span> ${this.name}`;
      }
    },
    series: []
  };

  ngOnChanges() {
    this.setChartInstance();
    this.updateChartOptions();
  }

  private setChartInstance(): void {
    const hasChartData = this.hasChartData();

    // If there’s no data, destroy the chart if it exists
    if (!hasChartData) {
      if (this.chartInstance) {
        this.chartInstance.destroy();
        this.chartInstance = undefined;
      }
      return;
    }

    // If there’s data, create or update the chart
    if (this.chartInstance) {
      this.chartInstance.update(this.chartOptions, true, true);
    } else {
      this.chartInstance = Highcharts.chart(this.containerId, this.chartOptions);
    }
  }

  private hasChartData(): boolean {
    return !!this.data?.dailyImpact?.length;
  }

  private updateChartOptions(): void {
    if (!this.chartInstance || !this.data?.dailyImpact?.length) {
      return;
    }

    const shortDisplayValuePipe = this.shortDisplayValuePipe;
    const series = this.getSeries(this.data);
    this.updateSeries(series);

    this.chartInstance.update({
      xAxis: {
        categories: this.data.dailyImpact.map(item => this.utilService.formatDateToShort(item.date)),
        labels: {
          rotation: -90,
          align: 'right'
        }
      },
      yAxis: [
        {
          title: {
            text: 'Impression Count',
          },
          labels: {
            formatter: function (this: any) {
              return shortDisplayValuePipe.transform(this.value, true);
            }
          },
          opposite: false
        },
        {
          title: {
            text: 'Brand Visibility',
          },
          labels: {
            formatter: function (this: any) {
              return addPercent(this.value);
            }
          },
          opposite: true
        }
      ],
    }, false);

    this.chartInstance.redraw();

  }

  private getSeries(data: AiPresenceAdvertisingImpactResponse): Highcharts.SeriesOptionsType[] {
    const impressionsSeries: Highcharts.SeriesSplineOptions = {
      name: 'Brand Impressions',
      type: 'spline',
      data: data.dailyImpact.map(item => item.impressions),
      yAxis: 0,
      color: '#AD588E',
      marker: { enabled: false }
    };

    const visibilitySeries: Highcharts.SeriesSplineOptions = {
      name: 'AI Visibility',
      type: 'spline',
      data: data.dailyImpact.map(item => item.visibility),
      yAxis: 1,
      color: '#FF8039',
      marker: { enabled: false }
    };

    return [impressionsSeries, visibilitySeries];
  }

  private updateSeries(series: Highcharts.SeriesOptionsType[]): void {
    if (!this.chartInstance) {
      return;
    }
    // Remove all existing series
    while (this.chartInstance.series.length) {
      this.chartInstance.series[this.chartInstance.series.length - 1].remove(false, false);
    }
    // Add new series
    series.forEach((s) => {
      this.chartInstance?.addSeries(s, false, false);
    });
  }

}
